import { getSchemaForFieldPath } from 'dashboard/components/JsonSchemaSelect/utils';
import {
  ConditionalRuleValueConfig,
  getConditionalRuleParts,
  RuleValueTypes,
} from 'dashboard/lib/workflows';
import { parseMethodChain } from 'dashboard/lib/workflows/method-chain';
import {
  fieldPathHasValidSchema,
  WorkflowValidationContext,
} from 'dashboard/lib/workflows/validation/validationContext';
import { BaseTrigger, BaseTriggerParams } from 'dashboard/models/trigger';
import {
  WorkflowConfigError,
  WorkflowErrorType,
  WorkflowOperatorKey,
} from 'dashboard/models/workflow';
import {
  INPUT_STEP_TOKEN,
  WorkflowConditional,
  WorkflowElse,
  WorkflowStepConditional,
} from 'dashboard/models/workflow-version';
import { JsonSchema, JsonSchemaTypes } from 'lib/json-schema';
import { hasNoPossibilities, isMultiValue, TokensToNode } from 'lib/workflows';

export const CATCHALL_ERROR_MSG = 'Last conditional must be catch all.';

const validateFieldPaths = (
  ruleValueConfig: ConditionalRuleValueConfig,
  graph: TokensToNode,
  jsonSchema: JsonSchema,
  token: string,
  andRulePath: string,
  validationContext: WorkflowValidationContext,
): WorkflowConfigError[] => {
  if (ruleValueConfig?.type !== RuleValueTypes.Field) {
    return [];
  }

  const errors: WorkflowConfigError[] = [];
  ruleValueConfig.paths.forEach((path) => {
    const configToken = parseMethodChain(path).links[0].name;
    if (
      ![BaseTrigger, BaseTriggerParams, INPUT_STEP_TOKEN].includes(configToken) &&
      graph[configToken] == null
    ) {
      errors.push({
        token,
        field: andRulePath,
        message: 'This rule is referencing a nonexistent step',
        type: WorkflowErrorType.Publish,
      });
    } else {
      const { valid, errorMessages } = fieldPathHasValidSchema(
        path,
        { jsonSchema },
        validationContext,
      );
      if (!valid) {
        errors.push({
          token,
          field: andRulePath,
          message: `${path} is invalid: ${errorMessages.join(', ')}`,
          type: WorkflowErrorType.Publish,
        });
      }
    }
  });

  return errors;
};

export const validateConditionalStep = (
  step: WorkflowStepConditional,
  validationContext: WorkflowValidationContext,
): WorkflowConfigError[] => {
  const { jsonSchema } = validationContext.getContextForStep(step.token);
  let errors: WorkflowConfigError[] = [];
  if (step.conditionals.length === 1 || step.branches.length === 0) {
    errors.push({
      token: step.token,
      field: 'conditionals',
      message: 'Must have at least one route',
      type: WorkflowErrorType.Publish,
    });
  } else {
    const lastConditional = step.conditionals[step.conditionals.length - 1];
    const conditionals = step.conditionals.slice(0, -1) as WorkflowConditional[]; // Slice out else conditional as it is always empty
    errors = errors.concat(
      conditionals.flatMap((conditional, i) =>
        validateConditionalRules(
          conditional,
          validationContext,
          step.token,
          jsonSchema,
          `conditionals[${i}].`,
        ),
      ),
      validateElseConditional(
        lastConditional,
        step.token,
        `conditionals[${step.conditionals.length - 1}].`,
      ),
    );
  }

  return errors;
};

export const validateConditionalRules = (
  conditional: WorkflowConditional,
  validationContext: WorkflowValidationContext,
  token: string,
  jsonSchema: JsonSchema,
  rootPath = 'conditional.',
): WorkflowConfigError[] => {
  const errors: WorkflowConfigError[] = [];
  const { graph } = validationContext;

  if (conditional.rules == null) {
    errors.push({
      token,
      field: `${rootPath}rules`,
      message: 'Must have at least one rule set',
      type: WorkflowErrorType.Publish,
    });
  } else {
    if (conditional.rules.or.length === 0) {
      errors.push({
        token,
        field: `${rootPath}rules.or`,
        message: 'Must have at least one rule set',
        type: WorkflowErrorType.Publish,
      });
    } else {
      const orRules = conditional.rules.or.flatMap((or) => or);
      orRules.forEach((orRule, orIndex) => {
        if (orRule.and.length === 0) {
          errors.push({
            token,
            field: `${rootPath}rules.or[${orIndex}].and`,
            message: 'Must have at least one rule',
            type: WorkflowErrorType.Publish,
          });
        } else {
          const andRules = orRule.and.flatMap((and) => and);
          andRules.forEach((andRule, andIndex) => {
            const { operator, valueLeftConfig, valueRightConfig } =
              getConditionalRuleParts(andRule);
            const numValues = hasNoPossibilities(operator) ? 1 : 2;
            const leftType = valueLeftConfig?.type;
            const rightValue = valueRightConfig?.value;
            const andRulePath = `${rootPath}rules.or[${orIndex}].and[${andIndex}]`;

            if (
              andRule[operator]?.length !== numValues ||
              !Object.values(WorkflowOperatorKey).includes(operator) ||
              leftType !== RuleValueTypes.Field ||
              (isMultiValue(operator) && Array.isArray(rightValue) && rightValue.length === 0)
            ) {
              errors.push({
                token,
                field: andRulePath,
                message: 'This rule is invalid',
                type: WorkflowErrorType.Publish,
              });
            } else {
              errors.push(
                ...validateFieldPaths(
                  valueLeftConfig,
                  graph,
                  jsonSchema,
                  token,
                  andRulePath,
                  validationContext,
                ),
              );

              switch (valueRightConfig?.type) {
                case RuleValueTypes.Field:
                  errors.push(
                    ...validateFieldPaths(
                      valueRightConfig,
                      graph,
                      jsonSchema,
                      token,
                      andRulePath,
                      validationContext,
                    ),
                  );
                  break;
                case RuleValueTypes.Value: {
                  valueLeftConfig.paths.forEach((path) => {
                    const { schema: fieldSchema } = getSchemaForFieldPath({
                      path,
                      initialSchema: jsonSchema,
                      primitiveUtilities: validationContext.workflowSchema.primitives,
                    });
                    if (
                      fieldSchema &&
                      'type' in fieldSchema &&
                      fieldSchema.type === JsonSchemaTypes.String &&
                      'enum' in fieldSchema &&
                      fieldSchema.enum &&
                      (isMultiValue(operator) && Array.isArray(valueRightConfig.value)
                        ? valueRightConfig.value.some(
                            (val) => !fieldSchema.enum?.includes(String(val)),
                          )
                        : // NB: list operators don't work like any other operator and generate their own right hand value options
                          ![WorkflowOperatorKey.InList, WorkflowOperatorKey.NotInList].includes(
                            operator,
                          ) && !fieldSchema.enum.includes(String(valueRightConfig.value)))
                    ) {
                      errors.push({
                        token,
                        field: andRulePath,
                        message: 'This rule has an invalid right-hand value',
                        type: WorkflowErrorType.Publish,
                      });
                    }
                  });
                  break;
                }
              }
            }
          });
        }
      });
    }
  }
  return errors;
};

const validateElseConditional = (
  conditional: WorkflowConditional | WorkflowElse,
  token: string,
  rootPath = '',
): WorkflowConfigError[] => {
  if (conditional.rules !== true) {
    return [
      {
        token,
        field: `${rootPath}rules`,
        message: CATCHALL_ERROR_MSG,
        type: WorkflowErrorType.Publish,
      },
    ];
  } else {
    return [];
  }
};

export const errorFieldMatchesConditionalBranchIndex = (
  field: string | undefined,
  branchIndex: number,
): boolean => {
  return !!field?.match(new RegExp(`^(conditionals|branches)\\[${branchIndex}\\]`));
};
